import { FC, useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { ACCOUNT_PERMISSIONS_CONFIG } from '@/components/ui/AccountTypeSelector';
import { ProductModel, CreateCardRequest, AccountPermissions } from '@/types';

interface CardCreationFormProps {
  cardType: 'week' | 'month';
  loading: boolean;
  onSubmit: (data: CreateCardRequest) => Promise<void>;
  onCancel?: () => void;
  // 新增：从外部传入账号类型，不在表单内选择
  accountType?: AccountPermissions;
}

// 产品型号配置
const PRODUCT_MODEL_CONFIG = {
  mini: {
    name: 'Mini版',
    description: '限制部分高级模型，适合体验使用',
    defaultConcurrency: 2,
    defaultDailyLimit: 25,
    color: 'bg-blue-50 border-blue-200 text-blue-800'
  },
  pro: {
    name: 'Pro版',
    description: '限制部分高级模型，适合个人5~6小时使用',
    defaultConcurrency: 3,
    defaultDailyLimit: 40,
    color: 'bg-purple-50 border-purple-200 text-purple-800'
  },
  max: {
    name: 'Max版',
    description: '限制部分高级模型，适合个人11~12小时使用',
    defaultConcurrency: 5,
    defaultDailyLimit: 80,
    color: 'bg-yellow-50 border-yellow-200 text-yellow-800'
  },
  maxplus: {
    name: 'Max Plus版',
    description: '企业级套餐，全模型访问权限，适合团队4~5人同时使用',
    defaultConcurrency: 15,
    defaultDailyLimit: 600,
    color: 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-300 text-purple-900'
  }
} as const;


export const CardCreationForm: FC<CardCreationFormProps> = ({
  cardType,
  loading,
  onSubmit,
  onCancel,
  accountType = 'claude'
}) => {
  const [formData, setFormData] = useState<CreateCardRequest>({
    productModel: 'mini',
    concurrencyLimit: PRODUCT_MODEL_CONFIG.mini.defaultConcurrency,
    dailyCostLimit: PRODUCT_MODEL_CONFIG.mini.defaultDailyLimit,
    tokenLimit: undefined,
    permissions: accountType
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // 同步外部传入的账号类型
  useEffect(() => {
    setFormData(prev => ({ ...prev, permissions: accountType }));
  }, [accountType]);

  // 处理产品型号变更
  const handleProductModelChange = (model: ProductModel) => {
    const config = PRODUCT_MODEL_CONFIG[model];
    setFormData(prev => ({
      ...prev,
      productModel: model,
      concurrencyLimit: config.defaultConcurrency,
      dailyCostLimit: config.defaultDailyLimit
    }));
    setErrors({});
  };

  // 表单验证
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.concurrencyLimit < 1 || formData.concurrencyLimit > 50) {
      newErrors.concurrencyLimit = '并发数必须在1-50之间';
    }

    if (formData.dailyCostLimit < 1 || formData.dailyCostLimit > 1000) {
      newErrors.dailyCostLimit = '每日限额必须在1-1000美元之间';
    }

    if (formData.tokenLimit && (formData.tokenLimit < 1000 || formData.tokenLimit > 10000000)) {
      newErrors.tokenLimit = 'Token限制必须在1,000-10,000,000之间';
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 提交处理
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    await onSubmit(formData);
  };

  const cardTypeText = cardType === 'week' ? '周卡' : '月卡';
  const validDays = cardType === 'week' ? 7 : 30;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          创建{cardTypeText} ({validDays}天有效期)
        </h3>
        <p className="text-sm text-gray-600">
          选择产品型号并配置卡片参数，系统将为您创建一个{validDays}天有效期的API Key
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* 产品型号选择 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            产品型号 *
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {Object.entries(PRODUCT_MODEL_CONFIG).map(([model, config]) => (
              <div
                key={model}
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  formData.productModel === model
                    ? config.color
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => handleProductModelChange(model as ProductModel)}
              >
                <div className="flex items-center">
                  <input
                    type="radio"
                    name="productModel"
                    value={model}
                    checked={formData.productModel === model}
                    onChange={() => handleProductModelChange(model as ProductModel)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <div className="ml-3">
                    <div className="text-sm font-medium text-gray-900">
                      {config.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {config.description}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 账号类型显示（不可编辑，由外部日卡页面统一管理） */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            账号类型（由日卡页面设置）
          </label>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center">
              <span className="mr-2 text-lg">{ACCOUNT_PERMISSIONS_CONFIG[accountType].icon}</span>
              <div>
                <div className="text-sm font-medium text-gray-900">
                  {ACCOUNT_PERMISSIONS_CONFIG[accountType].name}
                </div>
                <div className="text-xs text-gray-500">
                  {ACCOUNT_PERMISSIONS_CONFIG[accountType].description}
                </div>
              </div>
            </div>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            该{cardType === 'week' ? '周卡' : '月卡'}将使用日卡页面选择的账号类型
          </p>
        </div>

        {/* 基础配置 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="concurrencyLimit" className="block text-sm font-medium text-gray-700 mb-1">
              并发数 *
            </label>
            <input
              type="number"
              id="concurrencyLimit"
              min="1"
              max="50"
              value={formData.concurrencyLimit}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                concurrencyLimit: parseInt(e.target.value) || 1
              }))}
              className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                errors.concurrencyLimit ? 'border-red-300' : 'border-gray-300'
              }`}
              disabled={loading}
            />
            {errors.concurrencyLimit && (
              <p className="mt-1 text-xs text-red-600">{errors.concurrencyLimit}</p>
            )}
          </div>

          <div>
            <label htmlFor="dailyCostLimit" className="block text-sm font-medium text-gray-700 mb-1">
              每日限额 (美元) *
            </label>
            <input
              type="number"
              id="dailyCostLimit"
              min="1"
              max="1000"
              step="0.01"
              value={formData.dailyCostLimit}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                dailyCostLimit: parseFloat(e.target.value) || 1
              }))}
              className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                errors.dailyCostLimit ? 'border-red-300' : 'border-gray-300'
              }`}
              disabled={loading}
            />
            {errors.dailyCostLimit && (
              <p className="mt-1 text-xs text-red-600">{errors.dailyCostLimit}</p>
            )}
          </div>
        </div>

        {/* 高级设置 */}
        <div>
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center text-sm text-primary-600 hover:text-primary-700"
          >
            <span>{showAdvanced ? '隐藏' : '显示'}高级设置</span>
            <svg
              className={`ml-1 h-4 w-4 transform transition-transform ${
                showAdvanced ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showAdvanced && (
            <div className="mt-4 p-4 bg-gray-50 rounded-md">
              <div>
                <label htmlFor="tokenLimit" className="block text-sm font-medium text-gray-700 mb-1">
                  Token限制 (可选)
                </label>
                <input
                  type="number"
                  id="tokenLimit"
                  min="1000"
                  max="10000000"
                  value={formData.tokenLimit || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    tokenLimit: e.target.value ? parseInt(e.target.value) : undefined
                  }))}
                  placeholder="留空表示无限制"
                  className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                    errors.tokenLimit ? 'border-red-300' : 'border-gray-300'
                  }`}
                  disabled={loading}
                />
                {errors.tokenLimit && (
                  <p className="mt-1 text-xs text-red-600">{errors.tokenLimit}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  设置总Token使用限制，留空表示无限制
                </p>
              </div>
            </div>
          )}
        </div>

        {/* 卡片信息预览 */}
        <div className="bg-blue-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">卡片信息预览</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-blue-700">卡片类型:</span>
              <span className="ml-2 font-medium">{cardTypeText}</span>
            </div>
            <div>
              <span className="text-blue-700">有效期:</span>
              <span className="ml-2 font-medium">{validDays}天</span>
            </div>
            <div>
              <span className="text-blue-700">产品型号:</span>
              <span className="ml-2 font-medium">{PRODUCT_MODEL_CONFIG[formData.productModel].name}</span>
            </div>
            <div>
              <span className="text-blue-700">并发数:</span>
              <span className="ml-2 font-medium">{formData.concurrencyLimit}</span>
            </div>
            <div>
              <span className="text-blue-700">每日限额:</span>
              <span className="ml-2 font-medium">${formData.dailyCostLimit}</span>
            </div>
            <div>
              <span className="text-blue-700">Token限制:</span>
              <span className="ml-2 font-medium">
                {formData.tokenLimit ? formData.tokenLimit.toLocaleString() : '无限制'}
              </span>
            </div>
            <div>
              <span className="text-blue-700">账号类型:</span>
              <span className="ml-2 font-medium">
                {ACCOUNT_PERMISSIONS_CONFIG[formData.permissions || 'claude'].name}
              </span>
            </div>
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
            >
              取消
            </Button>
          )}
          <Button
            type="submit"
            disabled={loading}
            className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800"
          >
            {loading ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                创建中...
              </>
            ) : (
              <>
                🎫 创建{cardTypeText}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};